/*
 * Daruma — limites de quantité par commande.
 * Réglages : Paramètres du thème > Limites par commande (config rendue par snippets/daruma-order-limits-config.liquid).
 *
 * Point de passage unique : toutes les modifications du panier (page produit, offres bundle, tiroir, sélecteur de
 * couleur, duo) passent par fetch() vers /cart/add, /cart/change ou /cart/update. On intercepte ces appels :
 *  - ajout : si la limite est déjà atteinte, l'appel est refusé avec le message (le thème l'affiche sous le bouton) ;
 *            si l'ajout dépasserait la limite, la quantité est ramenée au maximum possible et un message s'affiche ;
 *  - modification de quantité : la quantité est ramenée au maximum et un message s'affiche.
 * Limite entendue par produit, toutes variantes confondues. Le paiement Shopify ne la vérifie pas côté serveur.
 */
(function () {
  'use strict';

  const configElement = document.getElementById('daruma-order-limits-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }
  if (!config || !config.enabled || !Array.isArray(config.limits)) return;

  const limits = config.limits.filter((limit) => limit && limit.productId && Number(limit.max) > 0);
  if (limits.length === 0) return;

  const limitByVariant = new Map();
  limits.forEach((limit) => {
    (limit.variantIds || []).forEach((variantId) => limitByVariant.set(String(variantId), limit));
  });
  const limitByProduct = new Map(limits.map((limit) => [String(limit.productId), limit]));

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);

  /* ---------- Panier courant (mis en cache, invalidé à chaque changement) ---------- */
  let cartCache = null;
  const invalidateCart = () => { cartCache = null; };
  document.addEventListener('cart:change', (event) => {
    const cart = event && event.detail && event.detail.cart;
    cartCache = cart && Array.isArray(cart.items) ? cart : null;
  });
  document.addEventListener('cart:refresh', invalidateCart);
  window.addEventListener('pageshow', invalidateCart);

  async function getCart() {
    if (cartCache) return cartCache;
    const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    cartCache = await response.json();
    return cartCache;
  }

  function quantityInCart(cart, productId, excludedLineKey) {
    return cart.items.reduce((total, item) => {
      if (String(item.product_id) !== String(productId)) return total;
      if (excludedLineKey && item.key === excludedLineKey) return total;
      return total + item.quantity;
    }, 0);
  }

  /* ---------- Message ---------- */
  function messageFor(limit, inCart) {
    let text = String(config.message || 'Limité à [max] par commande.');
    if (inCart > 0 && config.messageInCart) {
      text += ' ' + String(config.messageInCart);
    }
    return text
      .replace(/\[max\]/g, limit.max)
      .replace(/\[panier\]/g, inCart || 0)
      .replace(/\[produit\]/g, limit.title || '');
  }

  let toastElement = null;
  let toastTimer = null;
  function showToast(text) {
    if (!toastElement) {
      toastElement = document.createElement('div');
      toastElement.className = 'daruma-order-limit-toast';
      toastElement.setAttribute('role', 'status');
      toastElement.setAttribute('aria-live', 'polite');
      Object.assign(toastElement.style, {
        position: 'fixed',
        left: '50%',
        bottom: '24px',
        transform: 'translateX(-50%)',
        maxWidth: 'calc(100vw - 32px)',
        padding: '12px 18px',
        borderRadius: '999px',
        background: 'rgb(var(--text-color, 30 30 30))',
        color: 'rgb(var(--background, 255 255 255))',
        font: 'inherit',
        fontSize: '0.9rem',
        lineHeight: '1.4',
        textAlign: 'center',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        zIndex: '2147483000',
        opacity: '0',
        pointerEvents: 'none'
      });
      document.body.appendChild(toastElement);
    }
    toastElement.textContent = text;
    // Réapparition visible à chaque appel, même si le message précédent est encore affiché.
    toastElement.style.transition = 'none';
    toastElement.style.opacity = '1';
    if (typeof toastElement.animate === 'function') {
      toastElement.animate(
        [
          { opacity: 0, transform: 'translate(-50%, 12px) scale(0.96)' },
          { opacity: 1, transform: 'translate(-50%, 0) scale(1)' }
        ],
        { duration: 240, easing: 'ease-out' }
      );
    }
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastElement.style.transition = 'opacity 250ms ease';
      toastElement.style.opacity = '0';
    }, 4000);
  }

  function errorResponse(text) {
    return new Response(JSON.stringify({ status: 422, message: text, description: text }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /* ---------- Lecture / réécriture du corps de requête ---------- */
  function pathnameOf(input) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input);
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function bodyKind(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return 'formdata';
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return 'params';
    if (typeof body === 'string') return 'json';
    return null;
  }

  function readAddItems(body) {
    const kind = bodyKind(body);
    if (kind === 'formdata' || kind === 'params') {
      const items = [];
      if (body.has('id')) {
        items.push({ id: String(body.get('id')), quantity: parseInt(body.get('quantity'), 10) || 1, key: 'quantity' });
      } else {
        for (let index = 0; body.has(`items[${index}][id]`); index += 1) {
          items.push({
            id: String(body.get(`items[${index}][id]`)),
            quantity: parseInt(body.get(`items[${index}][quantity]`), 10) || 1,
            key: `items[${index}][quantity]`
          });
        }
      }
      return { kind, payload: null, items };
    }
    if (kind === 'json') {
      let payload;
      try { payload = JSON.parse(body); } catch (error) { return null; }
      if (!payload || typeof payload !== 'object') return null;
      const items = [];
      if (Array.isArray(payload.items)) {
        payload.items.forEach((item, index) => {
          items.push({ id: String(item.id), quantity: parseInt(item.quantity, 10) || 1, key: index });
        });
      } else if (payload.id != null) {
        items.push({ id: String(payload.id), quantity: parseInt(payload.quantity, 10) || 1, key: 'quantity' });
      }
      return { kind, payload, items };
    }
    return null;
  }

  function writeAddItems(body, parsed) {
    if (parsed.kind === 'formdata' || parsed.kind === 'params') {
      parsed.items.forEach((item) => body.set(item.key, String(item.quantity)));
      return body;
    }
    if (Array.isArray(parsed.payload.items)) {
      parsed.items.forEach((item) => { parsed.payload.items[item.key].quantity = item.quantity; });
    } else {
      parsed.payload.quantity = parsed.items[0].quantity;
    }
    return JSON.stringify(parsed.payload);
  }

  /* ---------- Règles ---------- */
  async function guardAdd(init) {
    const parsed = readAddItems(init.body);
    if (!parsed || parsed.items.length === 0) return init;

    const concerned = parsed.items.filter((item) => limitByVariant.has(item.id));
    if (concerned.length === 0) return init;

    const cart = await getCart();
    const requestedByProduct = new Map();
    concerned.forEach((item) => {
      const limit = limitByVariant.get(item.id);
      requestedByProduct.set(limit.productId, (requestedByProduct.get(limit.productId) || 0) + item.quantity);
    });

    let adjusted = false;
    for (const [productId, requested] of requestedByProduct) {
      const limit = limitByProduct.get(String(productId));
      const inCart = quantityInCart(cart, productId);
      const remaining = limit.max - inCart;
      if (remaining <= 0) {
        return { blocked: messageFor(limit, inCart) };
      }
      if (requested > remaining) {
        // On ramène les quantités de ce produit au maximum encore possible (premier article servi en priorité).
        let budget = remaining;
        concerned.forEach((item) => {
          if (limitByVariant.get(item.id).productId !== productId) return;
          const allowed = Math.min(item.quantity, budget);
          budget -= allowed;
          item.quantity = allowed;
        });
        adjusted = true;
        showToast(messageFor(limit, inCart));
      }
    }

    if (!adjusted) return init;
    // Un article ramené à zéro est retiré de la demande.
    if (Array.isArray(parsed.payload && parsed.payload.items)) {
      parsed.payload.items = parsed.payload.items.filter((item, index) => {
        const entry = parsed.items.find((candidate) => candidate.key === index);
        return !entry || entry.quantity > 0;
      });
      parsed.items = parsed.items.filter((item) => item.quantity > 0);
      parsed.items.forEach((item, index) => { item.key = index; });
      if (parsed.payload.items.length === 0) {
        const firstLimit = limitByVariant.get(concerned[0].id);
        return { blocked: messageFor(firstLimit, quantityInCart(cart, firstLimit.productId)) };
      }
    }
    return Object.assign({}, init, { body: writeAddItems(init.body, parsed) });
  }

  async function guardChange(init) {
    if (bodyKind(init.body) !== 'json') return init;
    let payload;
    try { payload = JSON.parse(init.body); } catch (error) { return init; }
    if (!payload || payload.quantity == null) return init;

    const cart = await getCart();
    let line = null;
    if (payload.line != null) {
      line = cart.items[parseInt(payload.line, 10) - 1] || null;
    } else if (payload.id != null) {
      line = cart.items.find((item) => item.key === String(payload.id) || String(item.variant_id) === String(payload.id)) || null;
    }
    if (!line) return init;

    const limit = limitByProduct.get(String(line.product_id));
    if (!limit) return init;

    const requested = parseInt(payload.quantity, 10) || 0;
    if (requested <= line.quantity) return init; // une baisse est toujours permise

    const allowed = Math.max(0, limit.max - quantityInCart(cart, line.product_id, line.key));
    if (requested <= allowed) return init;

    showToast(messageFor(limit, quantityInCart(cart, line.product_id)));
    // On ne descend jamais une ligne sous sa quantité actuelle (panier antérieur à la limite, par exemple).
    payload.quantity = Math.max(line.quantity, allowed);
    return Object.assign({}, init, { body: JSON.stringify(payload) });
  }

  async function guardUpdate(init) {
    if (bodyKind(init.body) !== 'json') return init;
    let payload;
    try { payload = JSON.parse(init.body); } catch (error) { return init; }
    if (!payload || !payload.updates) return init;

    const cart = await getCart();
    const updates = payload.updates;
    const isArray = Array.isArray(updates);

    // Quantité cible de chaque ligne après mise à jour.
    const targetByKey = new Map(cart.items.map((item) => [item.key, item.quantity]));
    const entries = isArray
      ? updates.map((quantity, index) => [cart.items[index] && cart.items[index].key, index, quantity])
      : Object.keys(updates).map((reference) => {
          const line = cart.items.find((item) => item.key === reference || String(item.variant_id) === reference);
          return [line && line.key, reference, updates[reference]];
        });
    entries.forEach(([key, , quantity]) => { if (key) targetByKey.set(key, parseInt(quantity, 10) || 0); });

    let adjusted = false;
    limits.forEach((limit) => {
      const lines = cart.items.filter((item) => String(item.product_id) === String(limit.productId));
      let total = lines.reduce((sum, item) => sum + (targetByKey.get(item.key) || 0), 0);
      if (total <= limit.max) return;
      // On réduit les lignes dont la quantité augmente, de la dernière à la première, sans jamais
      // descendre sous leur quantité actuelle, jusqu'à respecter la limite.
      for (let index = entries.length - 1; index >= 0 && total > limit.max; index -= 1) {
        const [key, reference] = entries[index];
        const line = lines.find((item) => item.key === key);
        if (!line) continue;
        const target = targetByKey.get(key) || 0;
        if (target <= line.quantity) continue;
        const reduced = Math.max(line.quantity, target - (total - limit.max));
        if (reduced === target) continue;
        total -= target - reduced;
        targetByKey.set(key, reduced);
        updates[reference] = reduced;
        adjusted = true;
      }
      if (adjusted) showToast(messageFor(limit, quantityInCart(cart, limit.productId)));
    });

    if (!adjusted) return init;
    payload.updates = updates;
    return Object.assign({}, init, { body: JSON.stringify(payload) });
  }

  /* ---------- Bouton « + » des sélecteurs de quantité ---------- */
  // Page produit : « + » alors que quantité choisie + quantité déjà au panier (toutes variantes) atteint la limite.
  // Tiroir panier : bouton rendu « limite atteinte » par quantity-selector.liquid.
  const LIMIT_CLASS = 'quantity-selector__button--daruma-limit';

  function increaseButtonOf(selector) {
    const input = selector.querySelector('input');
    const buttons = Array.from(selector.querySelectorAll('.quantity-selector__button'));
    // Le bouton « + » est celui qui suit le champ de saisie.
    return buttons.find((button) => input && (input.compareDocumentPosition(button) & Node.DOCUMENT_POSITION_FOLLOWING)) || null;
  }

  // Page produit : sélecteurs recalculés en direct à partir du panier (max, valeur, état du « + »).
  // Les lignes du tiroir (input[data-line-key]) sont re-rendues par le serveur à chaque changement.
  function refreshSelectors(cart) {
    document.querySelectorAll('quantity-selector[data-daruma-order-limit-max]').forEach((selector) => {
      const input = selector.querySelector('input');
      if (!input || input.hasAttribute('data-line-key')) return;
      const limit = limitByProduct.get(String(selector.dataset.darumaOrderLimitProduct));
      if (!limit) return;

      const inCart = quantityInCart(cart, limit.productId);
      const remaining = Math.max(1, limit.max - inCart);
      if (!('darumaBaseMax' in input.dataset)) {
        // max d'origine (stock, règles Shopify) hors limite Daruma : on ne descend jamais sous lui mais on le respecte
        input.dataset.darumaBaseMax = input.getAttribute('max') || '';
      }
      const baseMax = parseInt(input.dataset.darumaBaseMax, 10);
      const newMax = Number.isFinite(baseMax) && baseMax > 0 && baseMax < remaining ? baseMax : remaining;
      if (String(input.max) !== String(newMax)) input.max = String(newMax);

      const value = parseInt(input.value, 10) || 0;
      if (value > newMax) {
        input.value = String(newMax);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      const plus = increaseButtonOf(selector);
      if (plus) {
        const atLimit = (parseInt(input.value, 10) || 0) + inCart >= limit.max;
        plus.classList.toggle(LIMIT_CLASS, atLimit);
        if (atLimit) plus.setAttribute('aria-disabled', 'true'); else plus.removeAttribute('aria-disabled');
      }
      selector.dataset.darumaOrderLimitInCart = String(inCart);
    });
  }

  async function syncSelectors() {
    if (!document.querySelector('quantity-selector[data-daruma-order-limit-max]')) return;
    try {
      refreshSelectors(await getCart());
    } catch (error) {
      /* réseau indisponible : on laisse l'état rendu par Liquid */
    }
  }

  syncSelectors();
  document.addEventListener('cart:change', syncSelectors);
  document.addEventListener('cart:refresh', syncSelectors);
  document.addEventListener('variant:change', syncSelectors);
  document.addEventListener('cart-drawer:refreshed', syncSelectors);
  window.addEventListener('pageshow', syncSelectors);
  // Valeur tapée ou boutons -/+ : l'état du « + » suit la valeur
  document.addEventListener('input', (event) => {
    const selector = event.target.closest && event.target.closest('quantity-selector[data-daruma-order-limit-max]');
    if (selector && cartCache) refreshSelectors(cartCache);
  });
  // Re-rendu de la fiche produit (changement de variante) : les sélecteurs sont remplacés
  if (typeof MutationObserver === 'function') {
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => { scheduled = false; syncSelectors(); }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('quantity-selector[data-daruma-order-limit-max] .quantity-selector__button');
    if (!button) return;
    const selector = button.closest('quantity-selector');
    const limit = limitByProduct.get(String(selector.dataset.darumaOrderLimitProduct));
    if (!limit) return;

    const reached = button.hasAttribute('data-daruma-order-limit-reached'); // ligne du tiroir à la limite
    if (!reached && button !== increaseButtonOf(selector)) return;

    const input = selector.querySelector('input');
    const inCart = cartCache ? quantityInCart(cartCache, limit.productId) : (parseInt(selector.dataset.darumaOrderLimitInCart, 10) || 0);
    const value = parseInt(input && input.value, 10) || 0;
    const atLimit = reached || button.classList.contains(LIMIT_CLASS) || value + inCart >= limit.max;
    if (!atLimit) return;

    // On empêche l'incrément du thème et on explique.
    event.preventDefault();
    event.stopPropagation();
    showToast(messageFor(limit, inCart));
    getCart().then((cart) => {
      const freshInCart = quantityInCart(cart, limit.productId);
      if (freshInCart !== inCart) showToast(messageFor(limit, freshInCart));
      refreshSelectors(cart);
    }).catch(() => {});
  }, true);

  /* ---------- Interception ---------- */
  window.fetch = async function (input, init) {
    const pathname = pathnameOf(input);
    const isCartCall = /\/cart\/(add|change|update)(\.js)?$/.test(pathname);
    if (!isCartCall) return nativeFetch(input, init);

    let request = init || {};
    if (input instanceof Request && !request.body) {
      // Requête construite avec new Request() : on la déplie pour pouvoir relire son corps.
      const clone = input.clone();
      request = {
        method: input.method,
        headers: input.headers,
        body: /^(GET|HEAD)$/i.test(input.method) ? undefined : await clone.text()
      };
      input = input.url;
    }
    if (!request.body) return nativeFetch(input, init);

    try {
      let result;
      if (/\/cart\/add/.test(pathname)) result = await guardAdd(request);
      else if (/\/cart\/change/.test(pathname)) result = await guardChange(request);
      else result = await guardUpdate(request);

      if (result && result.blocked !== undefined) {
        invalidateCart();
        showToast(result.blocked);
        return errorResponse(result.blocked);
      }
      invalidateCart();
      return nativeFetch(input, result);
    } catch (error) {
      invalidateCart();
      return nativeFetch(input, init);
    }
  };
})();
